<template>
  <v-dialog v-model="show" max-width="400">
    <v-card>
      <v-card-title>
        {{
          selectedEnforcementOfficer
            ? $vuetify.lang.t(
                '$vuetify.ENFORCEMENT.ENFORCEMENT_OFFICERS.EDIT_OFFICER'
              )
            : $vuetify.lang.t(
                '$vuetify.ENFORCEMENT.ENFORCEMENT_OFFICERS.ADD_NEW_OFFICER'
              )
        }}
      </v-card-title>
      <v-form ref="form" v-model="formValid">
        <v-card-text>
          <v-text-field
            v-model="email"
            :disabled="selectedEnforcementOfficer.ID > 0"
            :label="
              $vuetify.lang.t('$vuetify.ENFORCEMENT.ENFORCEMENT_OFFICERS.EMAIL')
            "
            hide-details
            outlined
            required
            :rules="emailRules"
            type="email"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="enforcementOfficerName"
            :label="
              $vuetify.lang.t(
                '$vuetify.ENFORCEMENT.ENFORCEMENT_OFFICERS.OFFICER_NAME'
              )
            "
            hide-details
            outlined
            required
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="enforcementOfficerEnforcementPlaceGroupID"
            :items="enforcementPlaceGroups"
            item-text="Name"
            item-value="ID"
            :label="
              $vuetify.lang.t('$vuetify.ENFORCEMENT.ENFORCEMENT_OFFICERS.GROUP')
            "
            single-line
          ></v-select>
          <v-checkbox
            v-model="autoGeneratePassword"
            :label="$vuetify.lang.t('$vuetify.AUTO_GENERATE_PASSWORD')"
            :disabled="
              selectedEnforcementOfficer && selectedEnforcementOfficer.ID > 0
            "
            hide-details
          ></v-checkbox>
          <p v-if="!autoGeneratePassword" class="mt-3 mb-4">
            {{ $vuetify.lang.t('$vuetify.PASSWORD_CRITERIA_MISMATCH') }}
          </p>
          <v-text-field
            v-if="
              (!selectedEnforcementOfficer.ID ||
                selectedEnforcementOfficer.ID <= 0) &&
                !autoGeneratePassword
            "
            v-model="password"
            :label="$vuetify.lang.t('$vuetify.REGISTER.PASSWORD')"
            hide-details
            :required="
              !selectedEnforcementOfficer.ID ||
                selectedEnforcementOfficer.ID <= 0
            "
            outlined
            :rules="[passwordRules]"
            type="password"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-if="
              (!selectedEnforcementOfficer.ID ||
                selectedEnforcementOfficer.ID <= 0) &&
                !autoGeneratePassword
            "
            v-model="confirmPassword"
            :label="$vuetify.lang.t('$vuetify.REGISTER.PASSWORD_AGAIN')"
            hide-details
            :required="
              !selectedEnforcementOfficer.ID ||
                selectedEnforcementOfficer.ID <= 0
            "
            outlined
            type="password"
          ></v-text-field>
        </v-card-text>
      </v-form>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="show = false">
          {{ $vuetify.lang.t('$vuetify.CLOSE') }}
        </v-btn>
        <v-btn color="primary" class="secondary--text" :disabled="!formValid" @click="onSave()">
          {{ $vuetify.lang.t('$vuetify.SAVE') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'EditEnforcementOfficerDialog',
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      formValid: true,
      autoGeneratePassword: true,
      confirmPassword: ''
    }
  },
  computed: {
    ...mapGetters('app', ['loading']),
    ...mapGetters('enforcement', [
      'selectedEnforcementOfficer',
      'enforcementPlaceGroups'
    ]),
    show: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) {
          this.setSelectedEnforcementOfficer(undefined)
          this.$emit('close')
        }
      }
    },
    email: {
      get() {
        if (this.selectedEnforcementOfficer) {
          return this.selectedEnforcementOfficer.Email
        }
        return ''
      },
      set(val) {
        this.selectedEnforcementOfficer.Email = val
      }
    },
    password: {
      get() {
        if (this.selectedEnforcementOfficer) {
          return this.selectedEnforcementOfficer.Password
        }
        return ''
      },
      set(val) {
        this.selectedEnforcementOfficer.Password = val
      }
    },
    enforcementOfficerName: {
      get() {
        if (this.selectedEnforcementOfficer) {
          return this.selectedEnforcementOfficer.Name
        }
        return ''
      },
      set(val) {
        this.selectedEnforcementOfficer.Name = val
      }
    },
    enforcementOfficerEnforcementPlaceGroupID: {
      get() {
        if (this.selectedEnforcementOfficer) {
          return this.selectedEnforcementOfficer.EnforcementPlaceGroupID
        }
        return ''
      },
      set(val) {
        this.selectedEnforcementOfficer.EnforcementPlaceGroupID = val
      }
    },
    emailRules() {
      return [
        v => !!v || 'Email is required',
        v => {
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return re.test(String(v).toLowerCase()) || 'Email must be valid'
        }
      ]
    },

    passwordRules() {
      if (
        !this.selectedEnforcementOfficer.ID ||
        (this.selectedEnforcementOfficer.ID <= 0 && !this.autoGeneratePassword)
      ) {
        return v =>
          this.isStrongAndValidPassword(v) ||
          'Password must contain at least 8 characters, 1 number and 1 capital letter'
      }
      return true
    }
  },
  mounted() {
    if (this.enforcementPlaceGroups.length <= 0) {
      this.getEnforcementPlaceGroups()
    }
  },
  methods: {
    ...mapActions('enforcement', [
      'saveEnforcementOfficer',
      'getEnforcementPlaceGroups'
    ]),
    ...mapMutations('enforcement', ['setSelectedEnforcementOfficer']),
    isStrongAndValidPassword(password) {
      if (!password) {
        return false
      }
      // Check if password is at least 8 characters long
      if (password.length < 8) {
        return false
      }

      // Has at least one number
      if (!/\d/.test(password)) {
        return false
      }

      // Has at least one capital letter
      if (!/[A-Z]/.test(password)) {
        return false
      }

      return true
    },
    async onSave() {
      if (!this.formValid) return
      await this.saveEnforcementOfficer(this.autoGeneratePassword)
      this.show = false
    }
  }
}
</script>
